<div class="settings-container p-6 bg-gray-50 min-h-screen">
  <!-- Page Header: Premium Emerald-Teal Gradient matching application style pattern -->
  <div class="bg-gradient-to-r from-emerald-600 to-teal-800 text-white p-6 sm:p-8 rounded-xl shadow-md mb-8 transition-all duration-300">
    <h1 class="text-2xl sm:text-3xl font-bold tracking-tight">Global System Settings</h1>
    <p class="text-emerald-100 text-sm sm:text-base mt-2 opacity-90">Manage business charge rates, taxes, appointment intervals, and agent leave configurations</p>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex flex-col items-center justify-center py-20">
    <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mb-4"></div>
    <p class="text-gray-500 font-semibold">Loading system settings...</p>
  </div>

  <!-- Main Settings Form -->
  <div *ngIf="!isLoading && settings" class="space-y-8 animate-slide-up">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
      
      <!-- Card 1: General Business Rules -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between">
        <div>
          <h2 class="text-lg font-bold text-gray-800 border-b border-gray-100 pb-3 mb-5 flex items-center gap-2">
            <i class="fas fa-sliders-h text-emerald-600"></i> General Business Rules
          </h2>
          <div class="space-y-5">
            <!-- Booking Period -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Booking Window (Days)</label>
              <input type="number" [(ngModel)]="settings.bookingPeriodInDays" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 14" />
              <p class="text-xs text-gray-400 mt-1">Number of days in advance customers are allowed to book appointments.</p>
            </div>

            <!-- Ledger Past Days Limit -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Max Past Days for Ledger Entries (Days)</label>
              <input type="number" [(ngModel)]="settings.maxPastDaysForLedgerEntry" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 30" />
              <p class="text-xs text-gray-400 mt-1">Maximum number of days in the past allowed when recording agent ledger payments.</p>
            </div>
            
            <!-- Service Charge -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Standard Service Charge (₹)</label>
              <input type="number" [(ngModel)]="settings.serviceCharge" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 50" />
              <p class="text-xs text-gray-400 mt-1">Default flat booking fee charged for scrap collection visits.</p>
            </div>

            <!-- Waive Off Toggle -->
            <div class="bg-gray-50 p-4 rounded-xl border border-gray-150 space-y-4">
              <div class="flex items-center justify-between">
                <div>
                  <h3 class="text-sm font-bold text-gray-800">Waive Off Service Charge</h3>
                  <p class="text-xs text-gray-400">Allow waiving off standard booking fee for large collection tickets.</p>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" [(ngModel)]="settings.isWaiveOffEnabled" class="sr-only peer" />
                  <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              <!-- Waive Off Threshold (only relevant if enabled) -->
              <div *ngIf="settings.isWaiveOffEnabled" class="animate-fade-in">
                <label class="block text-sm font-semibold text-gray-700 mb-1.5">Minimum Bill for Waive Off (₹)</label>
                <input type="number" [(ngModel)]="settings.minBillForWaiveOff" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 300" />
                <p class="text-xs text-gray-400 mt-1">Service charge will be waived if the calculated total scrap value exceeds this amount.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 2: Financials, Tax & Commision -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between">
        <div>
          <h2 class="text-lg font-bold text-gray-800 border-b border-gray-100 pb-3 mb-5 flex items-center gap-2">
            <i class="fas fa-percent text-emerald-600"></i> Taxes & Commissions
          </h2>
          <div class="space-y-5">
            <!-- Platform Charge -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Platform Commission fee (%)</label>
              <div class="relative">
                <input type="number" [(ngModel)]="settings.platformChargeInPercent" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 pr-8 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 2" />
                <span class="absolute right-3.5 top-3 text-gray-400 text-sm font-semibold">%</span>
              </div>
              <p class="text-xs text-gray-400 mt-1">Percentage deducted from partner collections for using the platform.</p>
            </div>

            <!-- Base Subscription -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Base Subscription Amount (₹)</label>
              <input type="number" [(ngModel)]="settings.baseSubscriptionAmount" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 150" />
              <p class="text-xs text-gray-400 mt-1">Default flat subscription amount charged to subscribers.</p>
            </div>

            <!-- Tax configuration row (CGST + SGST) -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1.5">CGST Rate (%)</label>
                <div class="relative">
                  <input type="number" [(ngModel)]="settings.cgst" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 pr-8 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 9" />
                  <span class="absolute right-3.5 top-3 text-gray-400 text-sm font-semibold">%</span>
                </div>
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1.5">SGST Rate (%)</label>
                <div class="relative">
                  <input type="number" [(ngModel)]="settings.sgst" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 pr-8 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 9" />
                  <span class="absolute right-3.5 top-3 text-gray-400 text-sm font-semibold">%</span>
                </div>
              </div>
            </div>

            <!-- Voucher Commission -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Partner Voucher Commission (%)</label>
              <div class="relative">
                <input type="number" [(ngModel)]="settings.voucherCommissionPercentage" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 pr-8 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 10" />
                <span class="absolute right-3.5 top-3 text-gray-400 text-sm font-semibold">%</span>
              </div>
              <p class="text-xs text-gray-400 mt-1">Percentage charge applied to voucher redemptions settled with partner vendors.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Card 3: Agent Leaves & Penalty Rules -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between">
        <div>
          <h2 class="text-lg font-bold text-gray-800 border-b border-gray-100 pb-3 mb-5 flex items-center gap-2">
            <i class="fas fa-calendar-minus text-emerald-600"></i> Agent Attendance &amp; Penalties
          </h2>
          <div class="space-y-5">
            <!-- Quota configurations -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Monthly Leaves Limit</label>
              <input type="number" [(ngModel)]="settings.allowedLeavesPerMonth" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 5" />
            </div>
            <p class="text-xs text-gray-400">Default leaf quota allowed per month for agents.</p>

            <!-- Penalties configurations -->
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1.5">Informed Penalty (₹)</label>
                <input type="number" [(ngModel)]="settings.informedLeavePenaltyAmount" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 500" />
              </div>
              <div>
                <label class="block text-sm font-semibold text-gray-700 mb-1.5">Uninformed Penalty (₹)</label>
                <input type="number" [(ngModel)]="settings.uninformedLeavePenaltyAmount" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 1000" />
              </div>
            </div>
            <p class="text-xs text-gray-400">Penalty amounts deducted at month-end for standard penalized leaves vs explicit unplanned no-show absences.</p>
          </div>
        </div>
      </div>

      <!-- Card 4: Appointment Time Slots Manager -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between">
        <div>
          <h2 class="text-lg font-bold text-gray-800 border-b border-gray-100 pb-3 mb-5 flex items-center gap-2">
            <i class="fas fa-clock text-emerald-600"></i> Appointment Time Slots
          </h2>
          <div class="space-y-4">
            <!-- Add Slot Form -->
            <div class="flex gap-2">
              <input type="text" [(ngModel)]="newTimeSlot" (keyup.enter)="addTimeSlot()" placeholder="e.g. 14:00 - 16:00" class="flex-1 border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-sm text-gray-700 bg-white focus:outline-none transition-all duration-200" />
              <button type="button" (click)="addTimeSlot()" class="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all duration-200">
                <i class="fas fa-plus"></i> Add
              </button>
            </div>
            <p class="text-xs text-gray-400">Add time ranges in "HH:MM - HH:MM" format. E.g. "09:00 - 11:00".</p>

            <!-- Time Slots Chip List -->
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-2">Active Time Slots</label>
              <div class="flex flex-wrap gap-2.5 max-h-56 overflow-y-auto p-1.5 border border-gray-100 rounded-lg bg-gray-50">
                <span *ngFor="let slot of settings.appointmentTimeSlots; let i = index" class="inline-flex items-center gap-1.5 bg-white text-gray-800 border border-gray-200 rounded-full px-3 py-1 text-xs font-semibold shadow-sm animate-fade-in hover:border-emerald-300">
                  <span>{{ slot }}</span>
                  <button type="button" (click)="removeTimeSlot(i)" class="text-gray-400 hover:text-rose-600 focus:outline-none w-4 h-4 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
                    <i class="fas fa-times text-[10px]"></i>
                  </button>
                </span>
                <div *ngIf="settings.appointmentTimeSlots.length === 0" class="text-gray-400 italic text-xs p-4 w-full text-center">
                  No time slots configured. Add one above.
                </div>
              </div>
            </div>
          </div>
        </div>
      <!-- Card 5: Feedback Rules & Survey Questions -->
      <div class="bg-white rounded-xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between lg:col-span-2">
        <div>
          <h2 class="text-lg font-bold text-gray-800 border-b border-gray-100 pb-3 mb-5 flex items-center gap-2">
            <i class="fas fa-comment-dots text-emerald-600"></i> Feedback Rules & Survey Questions
          </h2>

          <!-- Feedback Rule Thresholds Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8 bg-gray-50 p-5 rounded-xl border border-gray-200">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">High Value Threshold (₹)</label>
              <input type="number" [(ngModel)]="settings.feedbackHighValueThreshold" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 10000" />
              <p class="text-xs text-gray-400 mt-1">Collections with value exceeding this amount are auto-tagged for feedback.</p>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Low Value Threshold (₹)</label>
              <input type="number" [(ngModel)]="settings.feedbackLowValueThreshold" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 500" />
              <p class="text-xs text-gray-400 mt-1">Collections with value below this amount are auto-tagged for feedback.</p>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">New Agent Tenure Threshold (Months)</label>
              <input type="number" [(ngModel)]="settings.feedbackNewAgentAgeMonths" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 3" />
              <p class="text-xs text-gray-400 mt-1">All collections by agents registered within this timeframe will be tagged.</p>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Senior Agent Tenure (Months)</label>
              <input type="number" [(ngModel)]="settings.feedbackSeniorAgentAgeMonths" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 12" />
              <p class="text-xs text-gray-400 mt-1">Agents registered for longer than this tenure qualify for periodic sampling.</p>
            </div>

            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1.5">Senior Agent Frequency (1 in X)</label>
              <input type="number" [(ngModel)]="settings.feedbackSeniorAgentFrequency" class="w-full border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-gray-700 bg-white focus:outline-none transition-all duration-200" placeholder="e.g. 3" />
              <p class="text-xs text-gray-400 mt-1">For senior agents, tag 1 out of every X collections (e.g. 3 = 1 in 3 collections).</p>
            </div>
          </div>

          <!-- Dynamic Questions Builder -->
          <div class="space-y-4">
            <h3 class="text-sm font-bold text-gray-800">Customer Care Call Survey Questions</h3>
            <p class="text-xs text-gray-400">Define questions to ask customers when gathering scrap collection feedback.</p>

            <!-- Add New Question Input Form -->
            <div class="flex flex-col md:flex-row gap-3 bg-emerald-50/50 p-4 rounded-xl border border-emerald-200">
              <input type="text" [(ngModel)]="newQuestionText" (keyup.enter)="addFeedbackQuestion()" placeholder="Enter question (e.g. Was the Agent wearing ID card?)" class="flex-1 border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-sm text-gray-700 bg-white focus:outline-none transition-all duration-200" />
              <select [(ngModel)]="newQuestionType" class="border-2 border-gray-150 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 rounded-lg p-2.5 text-sm text-gray-700 bg-white focus:outline-none transition-all duration-200">
                <option value="YesNo">Yes / No</option>
                <option value="Text">Open-ended Text</option>
                <option value="Rating">Rating (1-5)</option>
              </select>
              <button type="button" (click)="addFeedbackQuestion()" class="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2">
                <i class="fas fa-plus"></i> Add Question
              </button>
            </div>

            <!-- Active Questions List -->
            <div class="space-y-2 mt-4">
              <div *ngFor="let q of settings.feedbackQuestions; let i = index" class="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:border-emerald-300">
                <div class="flex items-center gap-3">
                  <span class="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center">{{ i + 1 }}</span>
                  <span class="text-sm font-semibold text-gray-800">{{ q.questionText }}</span>
                  <span class="px-2 py-0.5 text-xs font-semibold rounded bg-gray-100 text-gray-600 border border-gray-200">{{ q.questionType }}</span>
                </div>
                <button type="button" (click)="removeFeedbackQuestion(i)" class="text-gray-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition-colors">
                  <i class="fas fa-trash-alt"></i>
                </button>
              </div>

              <div *ngIf="!settings.feedbackQuestions || settings.feedbackQuestions.length === 0" class="text-gray-400 italic text-xs p-6 border-2 border-dashed border-gray-200 rounded-xl text-center">
                No feedback survey questions configured. Add questions using the form above.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Submit Action Area -->
    <div class="flex justify-end pt-4 border-t border-gray-250/80">
      <button (click)="saveSettings()" [disabled]="isSaving" class="px-8 py-3 bg-gradient-to-r from-emerald-600 to-emerald-700 text-white rounded-xl text-base font-bold shadow-md hover:shadow-lg hover:from-emerald-700 hover:to-emerald-800 transition-all duration-200 flex items-center gap-2">
        <i *ngIf="isSaving" class="fas fa-spinner animate-spin"></i>
        <span>{{ isSaving ? 'Saving Changes...' : 'Save Settings' }}</span>
      </button>
    </div>
  </div>

  <!-- Notification Toast -->
  <div *ngIf="notification.show" [ngClass]="{
    'bg-emerald-600 shadow-emerald-100': notification.type === 'success',
    'bg-rose-600 shadow-rose-100': notification.type === 'error',
    'bg-sky-600 shadow-sky-100': notification.type === 'info'
  }" class="fixed bottom-6 right-6 z-50 text-white px-6 py-4 rounded-xl shadow-2xl flex items-center gap-3 transition-all duration-300 animate-slide-up border border-white/10">
    <i [ngClass]="{
      'fa-check-circle': notification.type === 'success',
      'fa-exclamation-circle': notification.type === 'error',
      'fa-info-circle': notification.type === 'info'
    }" class="fas text-lg"></i>
    <span class="font-semibold text-sm">{{ notification.message }}</span>
  </div>
</div>
